import React, { useState } from 'react'

import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import moment from 'moment'
import { useHistory } from 'react-router-dom'

import Heading from 'presentation/shared/components/Heading'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import { Patient } from 'domain/entities/patient-model'
import { cpfMask } from 'presentation/utils/masks'
import SelectField from 'presentation/shared/components/SelectField'
import { Hospital } from 'domain/entities/hospital-model'
import { SurgicalOrderModel } from 'domain/entities/surgical-order-model'
import Button from 'presentation/shared/components/Button'
import { LoadSurgicalOrderDocument } from 'domain/usecases/surgical-order/load-surgical-order-document'
import downloadFileFromBlob from 'common/utils/downloadFileFromBlob'
import { getDocumentsLabelLocationByEnum } from 'presentation/utils/document-types-location'
import { ReactComponent as DownloadIcon } from 'presentation/assets/icons/download-icon.svg'
import { ReactComponent as ArrowLeft } from 'presentation/assets/icons/arrow-left2.svg'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import getAgeByBirthdayDate from 'common/utils/getAgeByBirthdayDate'

import * as S from './styles'
import { changesFirstLetterDocuments } from 'common/utils/changesFirstLetterDocuments'

type Props = {
  loadPatientInfo: Patient
  onlyVisualization: boolean
  surgicalOrder?: SurgicalOrderModel
  hospitals: Hospital[]
  loadSurgicalOrderDocument?: LoadSurgicalOrderDocument
} & WithLoadingProps

const ReviewCanceledOrder = WithLoading(
  ({
    loadPatientInfo,
    hospitals,
    surgicalOrder,
    loadSurgicalOrderDocument,
    setIsLoading
  }: Props) => {
    const [onlyVisualization] = useState(true)
    const birthDay = moment(loadPatientInfo.birthday).format('DD/MM/YYYY')
    const cancelledAt = moment
      .utc(surgicalOrder?.cancelledInfo?.cancelledAt)
      .format('DD/MM/YY - HH:mm')
    const solicitationDate = moment
      .utc(surgicalOrder?.cancellation_request?.created_at)
      .format('DD/MM/YY - HH:mm')

    const age = getAgeByBirthdayDate(birthDay)

    const history = useHistory()

    const { handleSubmit } = useFormik({
      initialValues: {
        cpf: loadPatientInfo.cpf,
        name: loadPatientInfo.name,
        gender: loadPatientInfo.gender,
        phone: loadPatientInfo.phone,
        email: loadPatientInfo.email,
        birthday: birthDay
      },
      enableReinitialize: true,
      onSubmit: async () => null
    })

    const handleDownlodDocument = async (document_id: number, type: string) => {
      try {
        if (surgicalOrder?.surgical_order_id) {
          setIsLoading(true)
          const surgicalOrderDocument = await loadSurgicalOrderDocument?.load(
            surgicalOrder?.surgical_order_id,
            document_id
          )
          if (surgicalOrderDocument) {
            downloadFileFromBlob(
              surgicalOrderDocument.data,
              surgicalOrderDocument.contentType,
              getDocumentsLabelLocationByEnum(type)
            )
          }
        }
      } catch (error) {
        toast.error('Falha ao realizar o download do arquivo')
      } finally {
        setIsLoading(false)
      }
    }

    const units = hospitals.map(({ name, friendly_name, hospital_id }) => ({
      label: friendly_name || name,
      value: hospital_id
    }))

    const optionUnits = [...units, { label: '', value: 0 }]

    return (
      <>
        <form onSubmit={handleSubmit}>
          <Heading as="h2" size={'medium'}>
            Dados do paciente
          </Heading>

          <S.Wrapper>
            <S.InputsContainer>
              <TextFieldNew
                key={'cpf'}
                label={'CPF'}
                name={'cpf'}
                mask={cpfMask}
                disabled={onlyVisualization}
                value={loadPatientInfo.cpf}
              />
              <TextFieldNew
                key={'name'}
                label={'Paciente'}
                name={'name'}
                disabled={onlyVisualization}
                value={loadPatientInfo.name}
              />
              <TextFieldNew
                key={'birthday'}
                label={'Data de nascimento'}
                name={'birthday'}
                disabled={onlyVisualization}
                value={birthDay}
              />
              <TextFieldNew
                key={'age'}
                label={'Idade'}
                name={'age'}
                disabled={onlyVisualization}
                value={`${age} anos`}
              />
            </S.InputsContainer>
            <S.InputsContainer>
              <TextFieldNew
                key={'gender'}
                label={'Sexo'}
                name={'gender'}
                disabled={onlyVisualization}
                value={loadPatientInfo.gender}
              />
              <TextFieldNew
                key={'phone'}
                label={'Celular'}
                name={'phone'}
                disabled={onlyVisualization}
                value={loadPatientInfo.phone}
              />
              <TextFieldNew
                key={'landlinePhone'}
                label={'Telefone fixo'}
                name={'landlinePhone'}
                disabled={onlyVisualization}
                value={loadPatientInfo.landlinePhone}
              />
              <TextFieldNew
                key={'email'}
                label={'E-mail'}
                name={'email'}
                disabled={onlyVisualization}
                value={loadPatientInfo.email}
              />
            </S.InputsContainer>
          </S.Wrapper>
        </form>

        <S.Container>
          <Heading as="h2" size={'medium'}>
            Unidade de realização
          </Heading>
          <SelectField
            label="Hospital *"
            name="unit"
            items={optionUnits}
            value={surgicalOrder?.hospital_id}
            style={{ width: 300 }}
            disabled
          />
        </S.Container>

        <S.Container>
          <Heading as="h2" size={'medium'}>
            Dados do convênio
          </Heading>
        </S.Container>

        <S.InputsContainer>
          <TextFieldNew
            label="Convênio"
            disabled
            defaultValue={surgicalOrder?.healthInsurance?.healthInsuranceName}
          />
          <TextFieldNew
            label="Plano de saúde"
            disabled
            defaultValue={surgicalOrder?.healthInsurance?.healthPlanName}
          />
          <TextFieldNew
            label="Número da carteira"
            disabled
            defaultValue={surgicalOrder?.healthInsurance?.healthCard}
          />
          <TextFieldNew
            label="Validade da carteira"
            disabled
            defaultValue={
              surgicalOrder?.healthInsurance?.validThru
                ? moment(surgicalOrder?.healthInsurance?.validThru).format(
                    'DD/MM/YYYY'
                  )
                : ''
            }
          />
          <TextFieldNew
            label="Empresa"
            disabled
            defaultValue={surgicalOrder?.healthInsurance?.company}
          />
        </S.InputsContainer>

        <S.Container>
          <Heading as="h2" size={'medium'}>
            Anexos
          </Heading>
        </S.Container>

        <S.BgContainer>
          <S.Documents>
            {surgicalOrder?.documents?.map((document) => (
              <S.Document key={document.document_id}>
                <Heading
                  as="h2"
                  size="small"
                  style={{ flex: 1, fontWeight: 400 }}
                >
                  {changesFirstLetterDocuments(document.type)}
                </Heading>
                <Button
                  variant="download"
                  fullWidth
                  onClick={() =>
                    handleDownlodDocument(document.document_id, document.type)
                  }
                  disabled={!onlyVisualization}
                  type="button"
                  style={{ width: 120, padding: '15px' }}
                >
                  <DownloadIcon className="icon" />
                  Download
                </Button>
              </S.Document>
            ))}
          </S.Documents>
        </S.BgContainer>

        {surgicalOrder?.cancellation_request?.requested_by && (
          <>
            <S.Container>
              <Heading as="h2" size="small" style={{ flex: 1 }}>
                Justificativa de solicitação de cancelamento
              </Heading>
              <S.CancelInputsContainers>
                <TextFieldNew
                  key={'solicitation'}
                  label={'Solicitante'}
                  name={'solicitation'}
                  disabled={onlyVisualization}
                  value={
                    surgicalOrder?.cancellation_request?.requested_by?.name
                  }
                />
                <TextFieldNew
                  key={'soliciationDate'}
                  label={'Data e horário da solicitação'}
                  name={'soliciationDate'}
                  disabled={onlyVisualization}
                  value={solicitationDate}
                />
                <TextAreaNew
                  key={'applicantsJustification'}
                  label={'Justificativa do solicitante'}
                  rows={3}
                  scrolling="auto"
                  name={'applicantsJustification'}
                  disabled={onlyVisualization}
                  value={surgicalOrder?.cancellation_request?.reason}
                />
                <TextAreaNew
                  key={'comment'}
                  rows={3}
                  scrolling="auto"
                  label={'Comentário'}
                  name={'comment'}
                  disabled={onlyVisualization}
                  value={
                    surgicalOrder?.cancellation_request?.comments?.[0]?.comment
                  }
                />
              </S.CancelInputsContainers>
            </S.Container>
          </>
        )}

        <S.Container>
          <Heading as="h2" size="small" style={{ flex: 1 }}>
            Cancelamento da CRMO
          </Heading>
          <S.CancelInputsContainers>
            <TextFieldNew
              key={'soliciationDate'}
              label={'Data e horário do cancelamento'}
              name={'solicitation'}
              disabled={onlyVisualization}
              value={cancelledAt}
            />
            <TextFieldNew
              key={'responsible'}
              label={'Responsável'}
              name={'responsible'}
              disabled={onlyVisualization}
              value={
                surgicalOrder?.cancellation_request?.canceled_by?.name !==
                undefined
                  ? surgicalOrder?.cancellation_request?.canceled_by?.name
                  : surgicalOrder?.cancelledInfo?.cancelledCollaboratorRegister
              }
            />
            <TextAreaNew
              key={'reasonCancel'}
              rows={3}
              scrolling="auto"
              label={'Motivo de cancelar o aviso cirúrgico'}
              name={'reasonCancel'}
              disabled={onlyVisualization}
              value={
                surgicalOrder?.cancellation_request?.reasons?.[0]
                  ?.mv_reason_description !== undefined
                  ? surgicalOrder?.cancellation_request?.reasons?.[0]
                      ?.mv_reason_description
                  : surgicalOrder?.justificationToDelete
              }
            />
            <TextAreaNew
              key={'observation'}
              rows={3}
              scrolling="auto"
              label={'Observações'}
              name={'observation'}
              disabled={onlyVisualization}
              value={
                surgicalOrder?.cancellation_request?.reasons?.[0]?.observation
              }
            />
          </S.CancelInputsContainers>
        </S.Container>

        <S.Container style={{ position: 'relative', marginBottom: 100 }}>
          <Button
            fullWidth
            onClick={() => history.push('/home')}
            disabled={!onlyVisualization}
            type="button"
            style={{
              width: 245,
              position: 'absolute',
              left: 0,
              fontSize: '16px'
            }}
          >
            <ArrowLeft className="icon" />
            Voltar para Dashboard
          </Button>
        </S.Container>
      </>
    )
  }
)

export default ReviewCanceledOrder
